import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import AgentArtistInviteBand from '@/components/landing/AgentArtistInviteBand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { ArrowLeft, Music, Check, X, Sparkles, Loader2 } from 'lucide-react';
import { checkSlugAvailability, generateUrlSlug } from '@/lib/utils';
import { isQrPromoReservedSlug, QR_SLUG_RESERVED_MESSAGE } from '@/lib/reservedUrlSlug';
import { COUNTRIES } from '@/lib/countries';
import SmartDateOfBirth from '@/components/SmartDateOfBirth';

const MobileSignupPage = () => {
  const [searchParams] = useSearchParams();
  const loginWithAgentHref = useMemo(() => {
    const a = searchParams.get('agent')?.trim();
    return a ? `/login?agent=${encodeURIComponent(a.toUpperCase())}` : '/login';
  }, [searchParams]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [country, setCountry] = useState('');
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const { signUp } = useAuth();

  // Real-time slug availability checking
  const checkSlugAvailabilityRealTime = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    if (isQrPromoReservedSlug(slug)) {
      setCheckingSlug(false);
      setSlugAvailable(false);
      return;
    }

    setCheckingSlug(true);
    try {
      const isAvailable = await checkSlugAvailability(slug);
      setSlugAvailable(isAvailable);
    } catch (error) {
      console.error('Error checking slug availability:', error);
      setSlugAvailable(false);
    } finally {
      setCheckingSlug(false);
    }
  };

  // Auto-generate profile URL from artist name
  const handleArtistNameChange = (e) => {
    const name = e.target.value;
    setArtistName(name);
    
    // Always update the profile URL as the user types
    if (name) {
      const generatedSlug = generateUrlSlug(name);
      setProfileUrl(generatedSlug);
      checkSlugAvailabilityRealTime(generatedSlug);
    } else {
      // Clear the profile URL if artist name is empty
      setProfileUrl('');
      setSlugAvailable(null);
    }
  };

  // Handle profile URL changes
  const handleProfileUrlChange = (e) => {
    const slug = generateUrlSlug(e.target.value);
    setProfileUrl(slug);
    checkSlugAvailabilityRealTime(slug);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for agent code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const agentCode = urlParams.get('agent');
    
    // Validate profile URL
    if (!profileUrl || profileUrl.length < 3) {
      alert('Please enter a valid profile URL (at least 3 characters)');
      return;
    }

    if (isQrPromoReservedSlug(profileUrl)) {
      alert(QR_SLUG_RESERVED_MESSAGE);
      return;
    }
    
    if (slugAvailable === false) {
      alert('This profile URL is already taken or unavailable. Please choose a different one.');
      return;
    }
    
    // Validate Full Name
    if (!fullName || fullName.trim().length < 2) {
      alert('Please enter your full legal name');
      return;
    }
    
    // Validate DOB
    if (!dateOfBirth || !dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year) {
      alert('Please select your complete date of birth');
      return;
    }
    
    // Validate age (must be 18+)
    const birthDate = new Date(dateOfBirth.year, dateOfBirth.month - 1, dateOfBirth.day);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
    
    if (actualAge < 18) {
      alert('You must be at least 18 years old to create an account');
      return;
    }
    
    // Validate checkboxes
    if (!acceptedPrivacyPolicy) {
      alert('Please accept the Privacy Policy to continue');
      return;
    }
    
    if (!acceptedTerms) {
      alert('Please accept the Terms and Conditions to continue');
      return;
    }

    if (!country || !String(country).trim()) {
      alert('Please select your country of residence');
      return;
    }
    
    setLoading(true);
    
    try {
      // Format DOB for backend (DD/MM/YYYY string for backward compatibility)
      const dobString = dateOfBirth.string || 
        `${String(dateOfBirth.day).padStart(2, '0')}/${String(dateOfBirth.month).padStart(2, '0')}/${dateOfBirth.year}`;
      
      const result = await signUp(email, password, {
          data: {
              fullName: fullName.trim(),
              artistName: artistName,
              profileUrl: profileUrl,
              dateOfBirth: dobString, // Backward compatible string format
              dateOfBirthObject: dateOfBirth, // Also pass the structured object
              country: country, // Pass 2-letter ISO country code
              agentCode: agentCode || null
          }
      });
      
      if (result.error) {
        setLoading(false);
      }
      // If successful, the AuthProvider will handle navigation
    } catch (err) {
      console.error('Signup error:', err);
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Artist Signup - Your Song Request</title>
        <meta name="description" content="Create your artist account and start managing song requests from your audience during live performances." />
        <meta property="og:title" content="Artist Signup - Your Song Request" />
        <meta property="og:description" content="Create your artist account and start managing song requests from your audience during live performances." />
      </Helmet>

      <div className="min-h-screen bg-black flex flex-col">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-cyan-900/20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(6,182,212,0.1),transparent_50%)]"></div>
        </div>

        <div className="relative z-10 w-full shrink-0 px-4 pt-6">
          <AgentArtistInviteBand mode="signup" />
        </div>

        {/* Header */}
        <div className="relative z-10 flex-shrink-0 pt-4 pb-4 px-4">
          <Link to="/" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="text-sm">Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.5)]">
                  <Music className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1">
                  <Sparkles className="w-4 h-4 text-yellow-400 animate-pulse" />
                </div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Create Artist Account</h1>
            <p className="text-cyan-200 text-sm">
              Join the platform and start getting paid to receive song requests
            </p>
          </motion.div>
        </div>

        {/* Form */}
        <div className="relative z-10 flex-1 px-4 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-sm mx-auto"
          >
            {/* Top Login Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-2"
            >
              <p className="text-gray-400 text-sm">
                Already have an account?{' '}
                <Link to={loginWithAgentHref} className="text-cyan-400 hover:text-cyan-300 font-semibold">
                  Login here
                </Link>
              </p>
            </motion.div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-cyan-100 text-sm font-medium">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                  placeholder="John Smith"
                />
                <p className="text-cyan-200 text-xs">
                  Your legal name (for payment processing)
                </p>
              </div>

              {/* Artist Name */}
              <div className="space-y-2">
                <Label htmlFor="artistName" className="text-cyan-100 text-sm font-medium">Artist/Stage Name</Label>
                <Input
                  id="artistName"
                  type="text"
                  value={artistName}
                  onChange={handleArtistNameChange}
                  required
                  className="bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                  placeholder="Your stage name"
                />
                <p className="text-cyan-200 text-xs">
                  The name that appears on your public page
                </p>
              </div>

              {/* Profile URL */}
              <div className="space-y-2">
                <Label htmlFor="profileUrl" className="text-cyan-100 text-sm font-medium">Profile URL</Label>
                <div className="relative">
                  <Input
                    id="profileUrl"
                    type="text"
                    value={profileUrl}
                    onChange={handleProfileUrlChange}
                    required
                    className={`bg-gray-900/50 border text-white placeholder-gray-400 pr-10 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)] ${
                      slugAvailable === true ? 'border-green-500/50 shadow-[0_0_10px_rgba(34,197,94,0.2)]' : 
                      slugAvailable === false ? 'border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 
                      'border-cyan-500/30'
                    }`}
                    placeholder="your-artist-name"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {checkingSlug && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500"></div>
                    )}
                    {!checkingSlug && slugAvailable === true && (
                      <Check className="h-4 w-4 text-green-400" />
                    )}
                    {!checkingSlug && slugAvailable === false && (
                      <X className="h-4 w-4 text-red-400" />
                    )}
                  </div>
                </div>
                <p className="text-cyan-200 text-xs">
                  yoursongrequest.com/{profileUrl || 'your-artist-name'}
                </p>
                {slugAvailable === false && (
                  <p className="text-red-400 text-xs">
                    {isQrPromoReservedSlug(profileUrl)
                      ? QR_SLUG_RESERVED_MESSAGE
                      : 'This URL is already taken. Please choose a different one.'}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-cyan-100 text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                  placeholder="your@email.com"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-cyan-100 text-sm font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                  placeholder="••••••••"
                />
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label htmlFor="country" className="text-cyan-100 text-sm font-medium">Country of Residence <span className="text-red-400">*</span></Label>
                <select
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  className="w-full bg-gray-900/50 border border-cyan-500/30 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                >
                  <option value="">Select your country...</option>
                  {COUNTRIES.map((countryOption) => (
                    <option key={countryOption.code} value={countryOption.code}>
                      {countryOption.label}
                    </option>
                  ))}
                </select>
                <p className="text-cyan-200 text-xs">
                  Please select your country of residence. This determines your currency and payment settings.
                </p>
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth" className="text-cyan-100 text-sm font-medium">
                  Date of Birth <span className="text-red-400">*</span>
                  {country === 'US' && (
                    <span className="text-cyan-200 text-xs ml-2">(MM/DD/YYYY format)</span>
                  )}
                  {country !== 'US' && (
                    <span className="text-cyan-200 text-xs ml-2">(DD/MM/YYYY format)</span>
                  )}
                </Label>
                    <SmartDateOfBirth
                      country={country}
                      value={dateOfBirth}
                      onChange={setDateOfBirth}
                      className="w-full bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20"
                    />
                <p className="text-cyan-200 text-xs">
                  Must be 18+ years old. Required for compliance and receiving payouts.
                </p>
              </div>

              {/* Privacy Policy and Terms */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="privacyPolicy"
                    checked={acceptedPrivacyPolicy}
                    onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 text-cyan-600 bg-gray-900/50 border-cyan-500/30 rounded focus:ring-cyan-500 flex-shrink-0"
                  />
                  <label htmlFor="privacyPolicy" className="text-xs text-cyan-200 cursor-pointer">
                    I accept the{' '}
                    <Link to="/privacy-policy" target="_blank" className="text-cyan-400 hover:text-cyan-300 underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 text-cyan-600 bg-gray-900/50 border-cyan-500/30 rounded focus:ring-cyan-500 flex-shrink-0"
                  />
                  <label htmlFor="terms" className="text-xs text-cyan-200 cursor-pointer">
                    I accept the{' '}
                    <Link to="/terms-of-service" target="_blank" className="text-cyan-400 hover:text-cyan-300 underline">
                      Terms and Conditions
                    </Link>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="pt-2"
              >
                <Button
                  type="submit"
                  disabled={loading || !acceptedPrivacyPolicy || !acceptedTerms}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold py-3 rounded-xl shadow-[0_0_20px_rgba(6,182,212,0.3)] border border-cyan-400/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </motion.div>
            </form>

            {/* Login Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-6 text-center"
            >
              <div className="mb-4 space-y-2 text-left">
                <div className="rounded-lg border border-purple-700/50 bg-purple-900/20 p-3">
                  <p className="text-purple-200 text-xs mb-1">🎤 Are you a DJ / Karaoke Host?</p>
                  <Link to="/karaoke/signup" className="text-purple-300 hover:text-purple-200 font-semibold text-xs">
                    Go to Karaoke Sign Up
                  </Link>
                </div>
                <div className="rounded-lg border border-blue-700/50 bg-blue-900/20 p-3">
                  <p className="text-blue-200 text-xs mb-1">💼 Are you a booking Agent?</p>
                  <Link to="/agent/signup" className="text-blue-300 hover:text-blue-200 font-semibold text-xs">
                    Go to Agent Sign Up
                  </Link>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Already have an account?{' '}
                <Link to={loginWithAgentHref} className="text-cyan-400 hover:text-cyan-300 font-semibold">
                  Login here
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MobileSignupPage;
